import { ChangeEvent, KeyboardEvent, useEffect, useState } from 'react';
import { Row, Col } from 'reactstrap';
import { IconInfoCircleFilled, IconTrash } from '@tabler/icons-react';
import {
    NSButton,
    NSCard,
    NSInput,
    NSLabel,
    NSSelect,
} from 'bricks';
import { IOptionValue } from 'bricks/types';
import { IPermanentDebtInfo, IPatchPermanentDebt } from 'views/ProFormaTable/types';
import { numeralFormatterCurrency } from 'ns_libs/formatter';
import { PAYOFF_PRIORITY_TOOLTIP_SINGLE, PAYOFF_PRIORITY_TOOLTIP_MULTIPLE } from 'views/ProFormaTable/constants';
import IconWithTooltip from 'components/IconWithTooltip/IconWithTooltip';
import MetricOutputCard from '../../../../MetricOutputCard/MetricOutputCard';
import PermanentDebtInputSteppers from '../PermanentDebtInputSteppers/PermanentDebtInputSteppers';
import NSIcon from 'bricks/NSIcon/NSIcon';

export interface IAdditionalLoanProps {
    loanData: IPermanentDebtInfo;
    loanNumber: number;
    saleMonth: number;
    showCents: boolean;
    priorityOptions: IOptionValue<number>[];
    onUpdate: (keyValues: Partial<IPatchPermanentDebt>) => void;
    onDelete?: () => void;
}

const AdditionalLoan = ({
    loanData,
    loanNumber,
    saleMonth,
    showCents,
    priorityOptions,
    onUpdate,
    onDelete,
}: IAdditionalLoanProps) => {
    const [loanName, setLoanName] = useState(loanData.name || '');
    const [loanAmount, setLoanAmount] = useState<number | null>(loanData.amount ?? loanData.loanFundingAmount ?? 0);

    useEffect(() => {
        setLoanName(loanData.name || '');
    }, [loanData.name]);

    useEffect(() => {
        setLoanAmount(loanData.amount ?? loanData.loanFundingAmount ?? 0);
    }, [loanData.amount, loanData.loanFundingAmount]);

    const handleNameBlur = () => {
        if (loanName !== (loanData.name || '')) {
            onUpdate({ name: loanName });
        }
    };

    const handleAmountBlur = () => {
        const normalizedAmount = Math.max(loanAmount ?? 0, 0);
        if (normalizedAmount !== (loanData.amount ?? loanData.loanFundingAmount ?? 0)) {
            onUpdate({ amount: normalizedAmount });
        }
    };

    return (
        <NSCard className="w-100 NSCard NSCard--level-3 my-1">
            <Row className="mx-n1 px-3 pt-2 pb-1">
                <div className="d-flex align-items-center justify-content-between w-100">
                    <div className="d-flex align-items-center">
                        <span className="mr-2">#{loanNumber}</span>
                        <NSInput
                            name="loanName"
                            type="text"
                            value={loanName}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => setLoanName(e.target.value)}
                            onBlur={handleNameBlur}
                            onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                                if (e.key === 'Enter') e.currentTarget.blur();
                            }}
                            placeholder="Untitled loan"
                        />
                    </div>
                    <NSButton
                        callback={onDelete}
                        ariaLabel="Delete loan"
                    >
                        <NSIcon icon={IconTrash} />
                    </NSButton>
                </div>
                <hr className="mt-1 w-100" />
                <Col className="px-1 pb-2" sm={12} md={6}>
                    <div className="d-flex align-items-center mt-2">
                        <NSLabel className="pl-0 col-5 my-auto font-weight-normal text-dark text-nowrap">
                            Loan amount
                        </NSLabel>
                        <div className="ProFormaPermanentDebt__input-width">
                            <NSInput
                                name="loanAmount"
                                type="number"
                                prependInputAddon="$"
                                value={loanAmount}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => setLoanAmount(
                                    e.target.value === '' ? null : parseFloat(e.target.value),
                                )}
                                onBlur={handleAmountBlur}
                                onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                                    if (e.key === 'Enter') e.currentTarget.blur();
                                }}
                                placeholder="Enter amount"
                                isRightAligned
                            />
                        </div>
                    </div>
                    <PermanentDebtInputSteppers
                        permanentDebtInfo={loanData}
                        saleMonth={saleMonth}
                        onUpdate={onUpdate}
                    />
                    <div className="d-flex align-items-center mt-2">
                        <div className="d-flex pl-0 col-5 my-auto text-dark">
                            <NSLabel className="font-weight-normal text-nowrap mr-1">
                                Payoff priority
                            </NSLabel>
                            <IconWithTooltip
                                name="payoffPriority"
                                icon={IconInfoCircleFilled}
                                tooltipText={priorityOptions.length > 1
                                    ? PAYOFF_PRIORITY_TOOLTIP_MULTIPLE
                                    : PAYOFF_PRIORITY_TOOLTIP_SINGLE}
                            />
                        </div>
                        <NSSelect
                            name="payoffPriority"
                            options={priorityOptions}
                            value={priorityOptions.find(
                                option => option.value === (loanData.payoffPriority ?? loanNumber),
                            )}
                            onChange={(option: IOptionValue<number>) => onUpdate({ payoffPriority: option.value })}
                            isClearable={false}
                            menuPortalTarget={document.body}
                            formClassName="ProFormaPermanentDebt__select-width"
                        />
                    </div>
                </Col>
                <Col className="px-1" sm={12} md={6}>
                    <Row className="mx-n1 pt-2">
                        <Col className="px-1 mb-2" sm={12} md={6}>
                            <MetricOutputCard
                                label="Fees & closing costs"
                                cardClassName="NSCard NSCard--level-4 p-2"
                                value={numeralFormatterCurrency(
                                    Math.abs(loanData.feesAndClosingCostsAmount || 0),
                                    showCents ? '$0,0.00' : '$0,0',
                                )}
                            />
                        </Col>
                        <Col className="px-1 mb-2" sm={12} md={6}>
                            <MetricOutputCard
                                label="Annual debt service"
                                cardClassName="NSCard NSCard--level-4 p-2"
                                value={numeralFormatterCurrency(
                                    loanData.debtService?.annualPayment || 0,
                                    showCents ? '$0,0.00' : '$0,0',
                                )}
                            />
                        </Col>
                    </Row>
                </Col>
            </Row>
        </NSCard>
    );
};

export default AdditionalLoan;
